import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Copy, Download, Upload, WandSparkles } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { countFields, lintDocument, type FormBindings, type FormRule, type FormSchema, type UiSchema } from "@ncp/forms";
import { Badge, Button, InlineAlert, Tabs, TabsList, TabsTrigger, cn, copyText, notifyDone, toast } from "@ncp/ui";
import { CodeEditor, jsonError } from "../../components/CodeEditor";
import type { FormBody, FormLint } from "./api";

type Part = "schema" | "uiSchema" | "rules" | "bindings" | "sampleData";
const PARTS: Part[] = ["schema", "uiSchema", "rules", "bindings", "sampleData"];

interface Props {
  body: FormBody;
  onChange: (next: FormBody) => void;
  readOnly?: boolean;
  serverLint?: FormLint | null;
  onServerLint?: () => void;
  linting?: boolean;
}

/**
 * Code view of the form: one CodeMirror editor per JSON part. Edits are parsed on every keystroke and applied to the body only
 * when the JSON is valid, so the visual tabs never see a half-typed document. Client lint runs live; server lint on demand.
 */
export function CodePanel({ body, onChange, readOnly, serverLint, onServerLint, linting }: Props) {
  const { t } = useTranslation();
  const [part, setPart] = useState<Part>("schema");
  const [text, setText] = useState(() => JSON.stringify(body[part] ?? (part === "rules" ? [] : {}), null, 2));
  const [dirtyPart, setDirtyPart] = useState(false);

  // Re-sync the editor whenever the selected part changes or the body changes from the visual side.
  const external = useMemo(() => JSON.stringify(body[part] ?? (part === "rules" ? [] : part === "sampleData" ? null : {}), null, 2), [body, part]);
  useEffect(() => { if (!dirtyPart) setText(external); }, [external, dirtyPart]);
  useEffect(() => { setText(external); setDirtyPart(false); }, [part]); // eslint-disable-line react-hooks/exhaustive-deps

  const error = useMemo(() => jsonError(text), [text]);
  const clientLint = useMemo(() => lintDocument(body.schema, body.uiSchema, body.rules), [body]);
  const fields = useMemo(() => countFields(body.schema), [body.schema]);

  const apply = (value: string) => {
    setText(value);
    setDirtyPart(true);
    if (jsonError(value)) return;
    const parsed = JSON.parse(value) as unknown;
    if (part === "rules" && !Array.isArray(parsed)) return;
    if (part !== "rules" && part !== "sampleData" && (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))) return;
    const next: FormBody = { ...body };
    if (part === "schema") next.schema = parsed as FormSchema;
    else if (part === "uiSchema") next.uiSchema = parsed as UiSchema;
    else if (part === "rules") next.rules = parsed as FormRule[];
    else if (part === "bindings") next.bindings = parsed as FormBindings;
    else next.sampleData = parsed as Record<string, unknown> | null;
    onChange(next);
    setDirtyPart(false);
  };

  const format = () => { if (!error) { const pretty = JSON.stringify(JSON.parse(text), null, 2); setText(pretty); } };
  const copy = () => void copyText(text, t("admin:common.copied"));
  const download = () => {
    const blob = new Blob([JSON.stringify({ name: body.name, nameAr: body.nameAr, purpose: body.purpose, schema: body.schema, uiSchema: body.uiSchema, rules: body.rules, bindings: body.bindings, sampleData: body.sampleData }, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${body.name.replace(/\s+/g, "-").toLowerCase() || "form"}.form.json`; a.click(); URL.revokeObjectURL(a.href);
  };
  const upload = () => {
    const input = document.createElement("input"); input.type = "file"; input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0]; if (!file) return;
      try {
        const parsed = JSON.parse(await file.text()) as Partial<FormBody>;
        if (!parsed.schema || !parsed.uiSchema) throw new Error(t("admin:forms.code.importInvalid"));
        onChange({ ...body, schema: parsed.schema, uiSchema: parsed.uiSchema, rules: parsed.rules ?? [], bindings: parsed.bindings ?? {}, sampleData: parsed.sampleData ?? null, ...(parsed.name ? { name: parsed.name } : {}), ...(parsed.nameAr ? { nameAr: parsed.nameAr } : {}) });
        notifyDone(t("admin:forms.code.imported"));
      } catch (e) { toast.error((e as Error).message); /* the file could not be read: no request was made */ }
    };
    input.click();
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Tabs value={part} onValueChange={(v) => setPart(v as Part)}>
            <TabsList>{PARTS.map((p) => <TabsTrigger key={p} value={p}>{t(`admin:forms.code.parts.${p}`)}</TabsTrigger>)}</TabsList>
          </Tabs>
          <div className="ms-auto flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={format} disabled={!!error}><WandSparkles /> {t("admin:forms.code.format")}</Button>
            <Button size="sm" variant="ghost" onClick={copy}><Copy /> {t("admin:common.copy")}</Button>
            <Button size="sm" variant="ghost" onClick={download}><Download /> {t("admin:common.export")}</Button>
            {!readOnly ? <Button size="sm" variant="ghost" onClick={upload}><Upload /> {t("admin:common.import")}</Button> : null}
          </div>
        </div>
        <CodeEditor value={text} onChange={apply} readOnly={readOnly} height="calc(100vh - 340px)" className={cn(error ? "ring-1 ring-danger-300" : "")} />
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          {error ? <span className="flex items-center gap-1 text-danger-700"><AlertTriangle className="size-3.5" /> {error}</span> : <span className="flex items-center gap-1 text-success-700"><Check className="size-3.5" /> {t("admin:forms.code.validJson")}</span>}
          <span className="ms-auto text-muted-foreground">{t("admin:forms.code.fieldCount", { count: fields })}</span>
        </div>
      </div>
      <aside className="space-y-3 xl:sticky xl:top-20 xl:self-start">
        <LintCard title={t("admin:forms.code.clientLint")} hint={t("admin:forms.code.clientLintHint")} errors={clientLint.errors} warnings={clientLint.warnings} />
        <div className="rounded-xl border border-sand-200 bg-white/80 p-3">
          <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold">{t("admin:forms.code.serverLint")}</h3>{serverLint ? serverLint.errors.length === 0 ? <Badge variant="success" size="sm" dot>{t("admin:common.valid")}</Badge> : <Badge variant="danger" size="sm" dot>{t("admin:common.issues", { count: serverLint.errors.length })}</Badge> : null}</div>
          <p className="mb-2 text-[11px] text-muted-foreground">{t("admin:forms.code.serverLintHint")}</p>
          {onServerLint ? <Button size="sm" variant="outline" className="w-full" loading={linting} onClick={onServerLint}>{t("admin:forms.code.runServerLint")}</Button> : null}
          {serverLint ? <div className="mt-2"><IssueLines errors={serverLint.errors} warnings={serverLint.warnings} /><p className="mt-1 text-[11px] text-muted-foreground">{t("admin:forms.code.serverFieldCount", { count: serverLint.fieldCount })}{serverLint.fieldCount !== fields ? ` · ${t("admin:forms.code.countMismatch", { client: fields })}` : ""}</p></div> : null}
        </div>
        <InlineAlert tone="info" className="text-xs">{t("admin:forms.code.xncpHint")}</InlineAlert>
      </aside>
    </div>
  );
}

function LintCard({ title, hint, errors, warnings }: { title: string; hint: string; errors: string[]; warnings: string[] }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-sand-200 bg-white/80 p-3">
      <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold">{title}</h3>{errors.length === 0 ? <Badge variant="success" size="sm" dot>{t("admin:common.valid")}</Badge> : <Badge variant="danger" size="sm" dot>{t("admin:common.issues", { count: errors.length })}</Badge>}</div>
      <p className="mb-2 text-[11px] text-muted-foreground">{hint}</p>
      <IssueLines errors={errors} warnings={warnings} />
    </div>
  );
}

function IssueLines({ errors, warnings }: { errors: string[]; warnings: string[] }) {
  const { t } = useTranslation();
  if (errors.length === 0 && warnings.length === 0) return <p className="text-xs text-success-700">{t("admin:forms.preview.noIssues")}</p>;
  return (
    <ul className="max-h-72 space-y-1 overflow-auto text-xs">
      {errors.map((e, i) => <li key={`e${i}`} className="rounded-md bg-danger-50 px-2 py-1 text-danger-800">{e}</li>)}
      {warnings.map((w, i) => <li key={`w${i}`} className="rounded-md bg-warning-50 px-2 py-1 text-warning-800">{w}</li>)}
    </ul>
  );
}
